import React, {
    ReactElement, useEffect, useMemo, useRef, useState,
} from "react";

import { DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import classNames from "classnames";
import { Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { createPortal } from "react-dom";
import { BiCollapseAlt } from "react-icons/bi";
import { BsPlusLg } from "react-icons/bs";
import {
    ActionContextType, FormField, FormFieldType, FormFieldVisibility,
    Workflow,
    WorkflowInput, WorkflowService, WorkflowServiceAction, WorkflowServiceChildAction,
    WorkflowServiceOutputParameter,
    WorkflowStep, WorkflowStepGroup, WorkflowStepInput,
    WorkflowStepOutputParameter,
} from "../../__generated__/api";
import Confirm from "../confirm/Confirm";
import { ReplacementGroup } from "../replacement-group";
import Button from "../theme/button/Button";
import DragDropContainer from "../theme/drag-drop/DragDropContainer";
import Styles from "./Workflow.module.scss";
import WorkflowCardCreate from "./WorkflowCardCreate";
import WorkflowIcon from "./WorkflowIcon";
import SortableList from "../theme/sortable/SortableList";
import createId from "../../create-id";

const ACTIONS_CREATED_ID = "urn:fq:workflow-service-action-child:(urn:fq:workflow-service-action:"
    + "(urn:fq:workflow-service:system,request-created),actions)";

const ACTIONS_CREATING_ID = "urn:fq:workflow-service-action-child:(urn:fq:workflow-service-action:"
    + "(urn:fq:workflow-service:system,request-creating),actions)";

const CREATING_ID = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,request-creating)";
const CREATED_ID = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,request-created)";
const GENERAL_ID = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,generic-no-ticket)";

const ACTIONS_GENERAL_ID = `urn:fq:workflow-service-action-child:(${GENERAL_ID},actions)`;

const ACTION_MAP: Record<string, ActionContextType> = {
    [CREATING_ID]: ActionContextType.Request,
    [CREATED_ID]: ActionContextType.Ticket,
    [GENERAL_ID]: ActionContextType.GenericNoTicket,
};

const ACTION_CREATING_REQUEST = {
    id: "",
    name: "Creating request",
    actionId: CREATING_ID,
    action: {
        id: CREATING_ID,
        name: "Request Creating",
        description: "Request first created",
        allowedChildren: [
            {
                id: ACTIONS_CREATING_ID,
                label: "Actions",
                description: "",
            },
        ],
    },
    serviceId: "urn:fq:workflow-service:system",
    description: "The automation to execute while the request is being created by the end user. "
        + "This can handle actions such conditionally showing or hiding fields.",
    parameters: [],
};
const ACTION_REQUEST_CREATED = {
    id: "",
    name: "Request Created",
    actionId: CREATED_ID,
    action: {
        id: CREATED_ID,
        name: "Request Created",
        description: "Request first created",
        allowedChildren: [
            {
                id: ACTIONS_CREATED_ID,
                label: "Actions",
                description: "",
            },
        ],
    },
    serviceId: "urn:fq:workflow-service:system",
    description: "The Automations to execute after the user has created the request. Common actions "
        + "include routing requests to appropriate people and sending approvals.",
    parameters: [],
};

const ACTION_REQUEST_GENERAL_INVOKED = {
    id: "",
    name: "General Invoked",
    actionId: GENERAL_ID,
    action: {
        id: GENERAL_ID,
        name: "Generic Called",
        description: "Request first created",
        allowedChildren: [
            {
                id: ACTIONS_GENERAL_ID,
                label: "Actions",
                description: "",
            },
        ],
    },
    serviceId: "urn:fq:workflow-service:system",
    description: "The automations to execute after the user has created the request. This allows "
        + "you to take actions such as firing off a web hook.",
    parameters: [],
};

export function createChildren() {
    const children: FormField[] = [];
    const creator = (
        row: WorkflowStepOutputParameter | WorkflowServiceOutputParameter | FormField,
    ) => {
        if (children.find(child => child.id === row.id)) {
            return;
        }
        if (row.type === FormFieldType.Users) {
            children.push({
                ...(row as FormField),
                id: row.id,
                name: row.name,
                description: row.description,
                type: row.type,
                isSystemField: false,
                visibility: FormFieldVisibility.Private,
            });
            children.push({
                id: `urn:fq:workflow-users-field:(${row.id},names)`,
                name: `${row.name} Name List`,
                description: row.description,
                type: FormFieldType.Text,
                isSystemField: true,
                visibility: FormFieldVisibility.Private,
            });
            children.push({
                id: `urn:fq:workflow-users-field:(${row.id},names-emails)`,
                name: `${row.name} Name and emails List`,
                description: row.description,
                type: FormFieldType.Text,
                isSystemField: true,
                visibility: FormFieldVisibility.Private,
            });
        } else if (row.type === FormFieldType.User) {
            children.push({
                ...(row as FormField),
                id: row.id,
                name: row.name,
                description: row.description,
                type: row.type,
                isSystemField: false,
                visibility: FormFieldVisibility.Private,
            });
            children.push({
                id: `urn:fq:workflow-user-field:(${row.id},name)`,
                name: `${row.name} Name`,
                description: row.description,
                type: FormFieldType.Text,
                isSystemField: true,
                visibility: FormFieldVisibility.Private,
            });
            children.push({
                id: `urn:fq:workflow-user-field:(${row.id},firstName)`,
                name: `${row.name} First Name`,
                description: row.description,
                type: FormFieldType.Text,
                isSystemField: true,
                visibility: FormFieldVisibility.Private,
            });
            children.push({
                id: `urn:fq:workflow-user-field:(${row.id},lastName)`,
                name: `${row.name} Last Name`,
                description: row.description,
                type: FormFieldType.Text,
                isSystemField: true,
                visibility: FormFieldVisibility.Private,
            });
            children.push({
                id: `urn:fq:workflow-user-field:(${row.id},email)`,
                name: `${row.name} Email`,
                description: row.description,
                type: FormFieldType.Text,
                isSystemField: true,
                visibility: FormFieldVisibility.Private,
            });
        } else {
            children.push({
                ...(row as FormField),
                id: row.id,
                name: row.name,
                description: row.description,
                type: row.type,
                isSystemField: (row as FormField).isSystemField ?? false,
                visibility: FormFieldVisibility.Private,
            });
        }
    };

    return { children, creator };
}

function WorkflowCard({
    item, className, onEdit, onDelete, level, bodyClassName,
}: {
    item: WorkflowStep;
    className?: string;
    bodyClassName?: string;
    onEdit: () => void;
    onDelete: () => void;
    level: number;

}): ReactElement {
    const [isHovering, setIsHovering] = useState(false);

    return (
        <Card
            className={`${className} cursor-pointer ${isHovering && level > 0 ? "shadow" : ""}`}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onFocus={() => setIsHovering(true)}
            onBlur={() => setIsHovering(false)}
        >
            <div className="px-3 py-2 border-1 rounded">
                <div className={classNames(
                    bodyClassName,
                    "d-flex card-title align-items-center justify-content-between m-0 py-1",
                )}
                >
                    <OverlayTrigger
                        placement="right"
                        delay={{ show: 250, hide: 0 }}
                        overlay={(
                            <Tooltip id={`tooltip-${item.id}`}>
                                {item.description ?? item.action?.description}
                            </Tooltip>
                        )}
                    >
                        <>
                            <WorkflowIcon
                                className={`text-light ${Styles["action-icon"]}`}
                                action={item.action as WorkflowServiceAction}
                                service={item.service as WorkflowService}
                            />
                            {level > 0 && (
                                <button
                                    className={classNames("btn btn-link p-0 m-0 flex-grow-1 text-start ms-2", Styles.card)}
                                    type="button"
                                    onClick={onEdit}
                                >
                                    <h6 className={`m-0 me-3 w-100 ${Styles.label}`}>
                                        {item.name ?? item.action?.name}
                                    </h6>
                                </button>
                            )}

                        </>
                    </OverlayTrigger>

                    {level > 0 && item.action?.id && (
                        <Button
                            isClose
                            variant="none"
                            className="text-light p-0"
                            size="xsmall"
                            aria-label="Remove step"
                            onClick={onDelete}
                        />
                    )}
                </div>
            </div>
        </Card>
    );
}
WorkflowCard.defaultProps = {
    className: "",
    bodyClassName: "",
};

interface DraggingContext {
    draggingId: string | undefined;
    draggingOverId: string | undefined;
    isDragging: boolean;
    isDraggingOver?: boolean;
    dragContainer?: FlattenResult;
    isParentDragging?: boolean;
}

function WorkflowRow({
    item, level, onUpdate, onEdit, onDelete, onCardCreate, input,
    allowedActionIds, type, className, draggingContext, isDragEnabled,
    rootStep,
}: {
    item: WorkflowStep;
    level: number;
    rootStep: WorkflowStep;
    onUpdate: () => void;
    onEdit: () => void;
    onDelete: () => void;
    onCardCreate: (action: WorkflowServiceChildAction | undefined) => void;
    isDragEnabled: boolean;
    input: ReplacementGroup[];
    allowedActionIds: string[];
    type: ActionContextType;
    draggingContext: DraggingContext | undefined;
    className?: string;
}): ReactElement {
    const [isHover, setIsHovering] = useState(false);
    const [deleteCandidate, setDeleteCandidate] = useState<{
        action: WorkflowStep;
        group: WorkflowStepGroup;
    }>();

    const isHovering = draggingContext?.isDragging || isHover || !item.action?.id;

    const [childAction, setChildAction] = useState<WorkflowServiceChildAction>();
    const [editInput, setEditInput] = useState<ReplacementGroup[]>();
    const [editItem, setEditItem] = useState<WorkflowStep>();
    const [allowedChildren, setAllowedChildren] = useState<string[]>();
    const hasChildren = item.children?.some(row => row.children?.isNotEmpty);

    const handleEditChild = (action: WorkflowStep, child: WorkflowServiceChildAction, childInput: ReplacementGroup[]) => {
        setEditItem(action);
        setChildAction(child);
        setEditInput(childInput);
        setAllowedChildren(undefined);
    };

    useEffect(() => {
        onCardCreate(childAction);
    }, [childAction]);

    const handleDeleteChild = (action: WorkflowStep, group: WorkflowStepGroup) => {
        setDeleteCandidate({ action, group });
        setAllowedChildren(undefined);
    };

    const confirmDelete = () => {
        if (!deleteCandidate) {
            return;
        }
        const { action, group } = deleteCandidate;
        if (!group.children) {
            return;
        }
        const deleteIndex = group.children.findIndex(row => row === action);
        if (deleteIndex >= 0) {
            group.children.splice(deleteIndex, 1);
            onUpdate();
        }
        setDeleteCandidate(undefined);
    };

    const cardCreate = (
        <WorkflowCardCreate
            show={childAction != null}
            onHide={() => {
                setChildAction(undefined);
                setEditItem(undefined);
            }}
            type={type}
            workflow={rootStep}
            allowedActionIds={allowedChildren ?? childAction?.allowedActionIds ?? []}
            action={editItem}
            input={editInput ?? input ?? []}
            serviceAction={editItem?.action as WorkflowServiceAction}
            onSave={card => {
                // eslint-disable-next-line no-param-reassign
                item.children = item.children ?? [];
                let container = item.children.find(child => child.groupId === childAction?.id);
                if (!container) {
                    container = {
                        groupId: childAction?.id,
                        children: [],
                    };
                    item.children.push(container);
                }
                if (!container.children) {
                    container.children = [];
                }
                const saveIndex = container.children.findIndex(child => child === editItem);

                if (saveIndex >= 0) {
                    container.children[saveIndex] = card;
                } else {
                    container.children?.push(card);
                }

                setChildAction(undefined);
                setEditItem(undefined);
                setEditInput(undefined);
                onUpdate();
            }}
        />
    );

    const isItemDragging = draggingContext?.isParentDragging || draggingContext?.draggingId === item?.id;
    const container = (
        <>
            {deleteCandidate && (
                <Confirm
                    shouldShow={deleteCandidate != null}
                    onHide={() => setDeleteCandidate(undefined)}
                    confirmText="Delete"
                    confirmVariant="danger"
                    onConfirm={confirmDelete}
                >
                    <span>
                        Are you sure you want to delete this
                        {" "}
                        &quot;
                        {deleteCandidate.action.name}
                        &quot;
                        {" "}
                        action?
                    </span>
                </Confirm>
            )}
            <div
                className={classNames(
                    "align-items-start d-flex",
                    isItemDragging ? Styles["invisible-child"] : "",
                )}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                onFocus={() => setIsHovering(true)}
                onBlur={() => setIsHovering(false)}
            >
                <div className="m-0">
                    <WorkflowCard
                        item={item}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        level={level}
                    />
                </div>

                {(
                    hasChildren || ((isHovering || draggingContext?.isDragging)
                        && (item.allowedChildren?.isNotEmpty || item.action?.allowedChildren?.isNotEmpty)))
                    && (
                        <div
                            className={classNames(
                                Styles["left-line"],
                                "ms-3",
                                isItemDragging ? Styles["invisible-child"] : "",
                            )}
                        >
                            {(item.allowedChildren ?? item.action?.allowedChildren)?.map(child => {
                                const group = item.children?.find(groupChild => groupChild.groupId === child.id);
                                if (!isHovering && !group) {
                                    return "";
                                }

                                let allowedChildrenIds: string[];
                                if (allowedActionIds?.isNotEmpty && child.allowedActionIds?.isNotEmpty) {
                                    allowedChildrenIds = allowedActionIds.union(child.allowedActionIds);
                                } else if (allowedActionIds?.isNotEmpty) {
                                    allowedChildrenIds = allowedActionIds;
                                } else {
                                    allowedChildrenIds = child.allowedActionIds as string[];
                                }

                                const droppableId = `${item.id}/${child.id}`;

                                const classes = [Styles["droppable-target"]];
                                if (group?.children?.isNotEmpty) {
                                    classes.push(Styles["left-line"], Styles["margin-start"]);
                                }

                                const isCurrentDraggingOver = draggingContext?.isDraggingOver;
                                // || (group?.children?.isEmpty && draggingContext?.isDragging);
                                if (isCurrentDraggingOver) {
                                    classes.push("border alert alert-light");
                                }

                                const allowedChildrenInput: ReplacementGroup[] = [];
                                const itemAllowedChildren = item.allowedChildren
                                    ?? item.action?.allowedChildren;
                                if (itemAllowedChildren?.isNotEmpty) {
                                    itemAllowedChildren
                                        .forEach(allowedActionChild => {
                                            if (allowedActionChild.output?.isNotEmpty) {
                                                const { children, creator } = createChildren();
                                                allowedActionChild.output.forEach(creator);
                                                allowedChildrenInput.push({
                                                    id: item?.id,
                                                    label: item.action?.name as string,
                                                    description: item.action?.description ?? "",
                                                    input: children,
                                                });
                                            }
                                        });
                                }
                                return (
                                    <div
                                        className={classNames(Styles["left-line"], Styles["left-line-block"], `ms-3 pb-4`)}
                                        key={child?.id}
                                    >
                                        <div
                                            className="d-flex align-items-center pt-2"
                                        >
                                            <button
                                                type="button"
                                                className="btn border p-1 px-2 rounded-pill me-3"
                                                aria-label="Create new action"
                                                onClick={() => {
                                                    const allowedParentGroup = (item.allowedChildren
                                                        ?? item.action?.allowedChildren);

                                                    const { children, creator } = createChildren();

                                                    if (item.output) {
                                                        item.output.forEach(creator);
                                                    }

                                                    if (allowedParentGroup) {
                                                        allowedParentGroup.forEach(parentGroup => {
                                                            if (parentGroup.output) {
                                                                parentGroup.output.forEach(creator);
                                                            }
                                                        });
                                                    }

                                                    if (item.output) {
                                                        item.output.forEach(creator);
                                                    }

                                                    const allowedParentInput = children.isEmpty ? [] : [{
                                                        id: item.id,
                                                        label: item.name,
                                                        description: item.description ?? "",
                                                        input: children,
                                                    } as ReplacementGroup];

                                                    setEditInput(input.concat(
                                                        allowedParentInput,
                                                        group?.children?.mapFilter(groupChild => {
                                                            const groupChildCreator = createChildren();

                                                            if (groupChild.output?.isNotEmpty) {
                                                                groupChild.output.forEach(groupChildCreator.creator);
                                                            }

                                                            if (groupChild.action?.output?.isNotEmpty) {
                                                                groupChild.action.output.forEach(groupChildCreator.creator);
                                                            }

                                                            return groupChildCreator.children.isEmpty ? undefined : {
                                                                id: groupChild.id,
                                                                label: groupChild.name,
                                                                description: groupChild.description ?? "",
                                                                input: groupChildCreator.children,
                                                            } as ReplacementGroup;
                                                        }) ?? [],
                                                    ));
                                                    setChildAction(child);
                                                    setAllowedChildren(allowedChildrenIds);
                                                }}
                                            >
                                                <BsPlusLg />
                                            </button>
                                            {child.label && (
                                                <OverlayTrigger
                                                    placement="right"
                                                    delay={{ show: 250, hide: 0 }}
                                                    overlay={child.description ? (
                                                        <Tooltip id={`tooltip-action-${child.id}`}>
                                                            {child.description}
                                                        </Tooltip>
                                                    ) : <div />}
                                                >
                                                    <h6 className="m-0">
                                                        {child.label}
                                                    </h6>
                                                </OverlayTrigger>
                                            )}

                                        </div>
                                        <DragDropContainer.Droppable
                                            id={droppableId}
                                            data={{
                                                group,
                                                child,
                                                step: item,
                                            }}
                                            isDisabled={isItemDragging}
                                            className={draggingContext?.isDraggingOver ? classNames(
                                                Styles["dragging-container"],
                                            ) : undefined}
                                        >
                                            <SortableContext
                                                items={(draggingContext?.dragContainer?.children
                                                    ?? group?.children
                                                    ?? []) as { id: string }[]}
                                                strategy={verticalListSortingStrategy}
                                                key={droppableId}
                                                id={droppableId}
                                            >

                                                {group?.children?.map((childItem, childIndex) => {
                                                    const groupChildren = group?.children?.limit(childIndex)
                                                        .mapFilter(groupChild => {
                                                            const { children, creator } = createChildren();

                                                            if (groupChild.output?.isNotEmpty) {
                                                                groupChild.output.forEach(creator);
                                                            }

                                                            if (groupChild.action?.output?.isNotEmpty) {
                                                                groupChild.action.output.forEach(creator);
                                                            }

                                                            return children.isEmpty ? undefined : {
                                                                id: groupChild.id,
                                                                label: groupChild.name,
                                                                description: groupChild.description ?? "",
                                                                input: children,
                                                            } as ReplacementGroup;
                                                        }) ?? [];

                                                    if (child.output?.isNotEmpty) {
                                                        const { children, creator } = createChildren();
                                                        child.output.forEach(creator);
                                                        groupChildren.push({
                                                            id: item?.id,
                                                            label: item.action?.name as string,
                                                            description: item.action?.description ?? "",
                                                            input: children,
                                                        });
                                                    }
                                                    const childrenInput = input.concat(
                                                        allowedChildrenInput,
                                                        groupChildren,
                                                    ).dedupe("id");
                                                    const isDraggingOver = draggingContext?.draggingOverId === droppableId;
                                                    return (
                                                        <WorkflowRow
                                                            key={childItem.id}
                                                            item={childItem}
                                                            level={level + 1}
                                                            rootStep={rootStep}
                                                            onUpdate={onUpdate}
                                                            onCardCreate={onCardCreate}
                                                            isDragEnabled={isDragEnabled}
                                                            draggingContext={draggingContext ? {
                                                                ...draggingContext,
                                                                isDraggingOver,
                                                                isParentDragging: isItemDragging,
                                                                isDragging: draggingContext.isDragging,
                                                            } : undefined}
                                                            type={type}
                                                            input={childrenInput}
                                                            allowedActionIds={allowedChildrenIds}
                                                            className={classNames(
                                                                classes.join(" "),
                                                            )}
                                                            onEdit={() => handleEditChild(
                                                                childItem,
                                                                child,
                                                                childrenInput,
                                                            )}
                                                            onDelete={() => handleDeleteChild(
                                                                childItem,
                                                                group as WorkflowStepGroup,
                                                            )}
                                                        />
                                                    );
                                                })}

                                                {draggingContext?.isDragging
                                                    && !isItemDragging
                                                    && (!group?.children || group?.children?.isEmpty)
                                                    && (
                                                        <div className="text-light py-4 w-100" />
                                                    )}

                                                {/* {dragChild.placeholder} */}
                                            </SortableContext>
                                        </DragDropContainer.Droppable>

                                    </div>
                                );
                            })}
                        </div>
                    )}

            </div>

        </>

    );

    return level === 0 ? (
        <div data-level={level} className={className}>
            {container}
            {cardCreate}
        </div>
    ) : (

        <SortableList.Item
            id={item.id}
            data-level={level}
            item={item as { id: string }}
            isEnabled={isDragEnabled}
            className={classNames(
                draggingContext?.isDraggingOver ? "" : Styles["left-line"],
                Styles["draggable-card"],
                `pb-2 pt-2`,
                className,
            )}
        >
            {container}
            {cardCreate}
        </SortableList.Item>

    );
}

WorkflowRow.defaultProps = {
    className: "",
};

export function createWorklowStep(item: WorkflowStep): WorkflowStepInput {
    return {
        id: item.id || undefined,
        actionId: item.actionId || undefined,
        serviceId: item.serviceId || undefined,
        integrationId: item.integrationId,
        name: item.name,
        description: item.description,
        allowedChildren: item.allowedChildren?.map(child => ({
            description: child.description,
            id: child.id,
            label: child.label,
            allowedActionIds: child.allowedActionIds,
            output: child.output?.map(out => ({
                id: out.id,
                description: out.description,
                name: out.name,
                type: out.type,
            })),
        })),
        parameters: item.parameters?.map(param => ({
            value: {
                fieldId: param.value.fieldId as string,
                type: param.value.type,
                boolean: param.value.boolean,
                date: param.value.date,
                displayValue: param.value.displayValue,
                number: param.value.number,
                options: param.value.options,
                string: param.value.string,
            },
            replacement: param.replacement?.map(replace => ({
                fieldId: replace.fieldId,
                groupId: replace.groupId,
                replacement: replace.replacement,
            })),
        })),
        output: item.output?.map(param => ({
            description: param.description,
            name: param.name,
            type: param.type,
            id: param.id,
        })),
        children: item.children?.map(child => ({
            groupId: child.groupId || undefined,
            children: child.children?.map(createWorklowStep),
        })),
    };
}

export function createInput(item: Workflow): WorkflowInput {
    return {
        name: item.name,
        description: item.description,
        iconUrl: item.iconUrl,
        steps: item.steps?.map(row => createWorklowStep(row)) ?? [],
    };
}

export function removeTypename(steps: WorkflowStep[]): WorkflowStep[] {
    return steps.map(step => {
        const copy = { ...step };
        // eslint-disable-next-line no-param-reassign
        delete copy.__typename;
        if (step.children) {
            copy.children = step.children.map(group => ({
                groupId: group.groupId,
                children: group.children ? removeTypename(group.children) : undefined,
            }));
        }
        copy.output = step.output?.map(value => ({ ...value }));
        copy.parameters = step.parameters?.map(param => {
            const paramCopy = {
                ...param,
            };
            delete paramCopy.__typename;
            paramCopy.value = { ...paramCopy.value };
            delete paramCopy.value.__typename;
            paramCopy.replacement = param.replacement?.map(replace => {
                const replaceCopy = { ...replace };
                delete replaceCopy.__typename;
                return replaceCopy;
            });

            return paramCopy;
        });

        return copy;
    });
}

function createEmpty({
    shouldShowCreatingRequest,
    isGenericNoTicket,
}: {
    shouldShowCreatingRequest?: boolean;
    isGenericNoTicket?: boolean;
}): WorkflowStep[] {
    const workflow = [];

    if (isGenericNoTicket) {
        workflow.push({
            ...ACTION_REQUEST_GENERAL_INVOKED,
            id: `urn:fq:workflow-step:(urn:fq:workflow:-1,${createId()})`,
        });
    } else {
        workflow.push({
            ...ACTION_REQUEST_CREATED,
            id: `urn:fq:workflow-step:(urn:fq:workflow:-1,${createId()})`,
        });
    }

    if (shouldShowCreatingRequest) {
        workflow.push({
            ...ACTION_CREATING_REQUEST,
            id: `urn:fq:workflow-step:(urn:fq:workflow:-1,${createId()})`,
        });
    }

    return workflow.map(row => ({
        ...row,
        children: [],
        parameters: [],
    }));
}

interface FlattenResult {
    group: WorkflowStepGroup;
    groupId: string;
    id: string;
    children: WorkflowStep[];
    parent: WorkflowStep;
}

function flatten(steps: WorkflowStep[]): FlattenResult[] {
    return steps.flatMap(row => {
        const taken = row.children?.map(child => ({
            id: `${row.id}/${child.groupId}`,
            groupId: child.groupId,
            parent: row,
            group: child,
            children: child.children as WorkflowStep[],
        }));

        const children = row.children?.flatMap(group => (group.children ? flatten(group.children) : undefined)).filterNull();

        const childrenTaken = new Set(row.children?.toIdList("groupId"));
        const allowed = row.action?.allowedChildren?.filterMap(child => {
            const groupChildren = [] as WorkflowStep[];
            return {
                id: `${row.id}/${child.id}`,
                groupId: child.id,
                parent: row,
                group: {
                    groupId: child.id,
                    children: groupChildren,
                },
                children: groupChildren,
            };
        }, child => !childrenTaken.has(child.id));

        return (taken as FlattenResult[] ?? []).concat(allowed ?? [], children ?? []);
    }).filterNull();
}

export interface WorkflowProperties {
    input?: ReplacementGroup[];
    onChange?: (workflow: WorkflowStep[]) => void;
    value?: WorkflowStep[];
    shouldShowCreatingRequest?: boolean;
    isModal?: boolean;
    onHideModal?: () => void;
    type?: ActionContextType;
}

export default function WorkflowDisplay({
    input, onChange, value, shouldShowCreatingRequest, isModal, onHideModal, type,
}: WorkflowProperties): ReactElement {
    const [current, setCurrent] = useState<WorkflowStep[]>(() => createEmpty({
        shouldShowCreatingRequest,
        isGenericNoTicket: type === ActionContextType.GenericNoTicket || type === ActionContextType.Workspace,
    }));
    const flatGroups = useMemo(() => flatten(current), [current]);
    const [draggingContext, setDraggingContext] = useState<string>();
    const [draggingId, setDraggingId] = useState<string>();
    const [draggingItem, setDraggingItem] = useState<WorkflowStep>();
    const currentDragContainer = useRef<FlattenResult>();
    const [dragContainer, setDragContaner] = useState<FlattenResult>();
    const [isEditing, setIsEditing] = useState(false);

    const parentDivRef = useRef<HTMLDivElement>(null);

    const inputGroups = useMemo(() => {
        const groups = input ? [...input] : [];
        const index = groups.findIndex(group => group.id === "urn:fq:form-field-group:request");
        const requestGroup = groups[index];
        if (requestGroup?.input) {
            const { children, creator } = createChildren();
            requestGroup.input.forEach(creator);
            groups[index] = {
                ...requestGroup,
                input: children,
            };
        }

        return groups;
    }, [input]);

    useEffect(() => {
        if (value?.isNotEmpty) {
            const workflows = removeTypename(value);
            if (workflows.length === 1 && shouldShowCreatingRequest) {
                workflows.unshift(ACTION_CREATING_REQUEST);
            }
            setCurrent(workflows);
        } else {
            setCurrent(createEmpty({
                shouldShowCreatingRequest,
                isGenericNoTicket: type === ActionContextType.GenericNoTicket || type === ActionContextType.Workspace,
            }));
        }
    }, [value]);

    const display = current?.map(row => (
        <DragDropContainer
            className={classNames("mb-4", Styles["drag-drop-container"])}
            nonce={window.NONCE}
            key={row.id || row.actionId}
            onDragEnd={(event: DragEndEvent) => {
                setDraggingContext(undefined);
                setDraggingId(undefined);
                if (currentDragContainer.current?.children) {
                    const group = flatGroups.find(item => item.id === currentDragContainer.current?.id);
                    const items = group?.children;
                    if (items) {
                        const oldIndex = items.findIndex(item => item.id === event.active.id.toString());
                        const moveItem = items[oldIndex];
                        if (moveItem) {
                            delete (moveItem as unknown as { sortable: unknown }).sortable;
                        }

                        const newIndex = items.findIndex(item => item.id === event.over?.id.toString());
                        if (group && newIndex != null && newIndex >= 0 && oldIndex != null && oldIndex >= 0) {
                            group.group.children = arrayMove(items, oldIndex, newIndex);
                        }
                    }
                }

                const copyRemoved = removeTypename(current);
                setCurrent(copyRemoved);
                onChange?.(copyRemoved);
            }}
            onDragOver={event => {
                const childAction = event.over?.data.current?.child as WorkflowServiceChildAction;
                const step = event.over?.data.current?.step as WorkflowStep;

                const item = event.active.data.current as WorkflowStep;
                if (!item.id) {
                    return;
                }

                const groupId: string | undefined = step && childAction ? `${step?.id}/${childAction?.id}` : undefined;
                // if (!childAction || !step) {
                //     const sortable = event.active.data.current as {sortable: {containerId: string}, id: string};
                //     if (sortable && sortable.id !== item.id) {
                //         const sortableId = sortable.sortable?.containerId;
                //         const container = flatGroups.find(groupItem => groupItem.id === sortableId);
                //         groupId = container?.id;
                //     }
                // }

                // const group = event.over?.data.current?.group as WorkflowStepGroup;
                if (!groupId || currentDragContainer.current?.groupId === groupId) {
                    return;
                }

                let groupRecord = flatGroups.find(groupItem => groupItem.id === groupId);

                if (!groupRecord) {
                    const group = {
                        groupId: childAction.id,
                        children: [],
                    };
                    groupRecord = {
                        group,
                        children: group.children as WorkflowStep[],
                        groupId: group.groupId,
                        id: groupId,
                        parent: step,
                    };
                    flatGroups.push(groupRecord);
                    step.children ??= [];
                    step.children.push(group);
                }

                if (currentDragContainer.current?.children) {
                    const oldIndex = currentDragContainer.current.children.findIndex(child => child.id === item.id);
                    if (oldIndex >= 0) {
                        currentDragContainer.current.children.splice(oldIndex, 1);
                    }
                }

                if (groupRecord?.parent) {
                    groupRecord.parent.children ??= [];
                    if (!groupRecord.parent.children.some(child => child.groupId === groupRecord?.groupId)) {
                        groupRecord.parent.children.push(groupRecord.group);
                    }
                }

                groupRecord?.children?.push(item);
                currentDragContainer.current = groupRecord;
                setDragContaner(groupRecord);
            }}
            onDragStart={event => {
                setDraggingContext(row.id);
                setDraggingId(event.active.id as string);
                setDraggingItem(event.active.data.current as WorkflowStep);
                const sortable = event.active.data.current as { sortable: { containerId: string } };
                const sortableId = sortable.sortable?.containerId;
                const container = flatGroups.find(item => item.id === sortableId);
                currentDragContainer.current = container;
            }}
        >

            {draggingItem && createPortal(
                <DragDropContainer.DragOverlay>
                    <WorkflowCard
                        item={draggingItem}
                        className={Styles["dragging-card"]}
                        onEdit={() => {
                            // nope
                        }}
                        onDelete={() => {
                            // nope
                        }}
                        level={1}
                    />
                </DragDropContainer.DragOverlay>,
                parentDivRef.current ?? document.body,
            )}

            {current.length > 1 && (
                <>
                    <h6>{row.name}</h6>
                    <small className="text-light">
                        {row.description}
                    </small>
                </>
            )}

            <WorkflowRow
                key={row.id || row.actionId}
                item={row}
                level={0}
                rootStep={row}
                className="mb-3"
                draggingContext={draggingContext ? {
                    isDragging: draggingContext === row.id,
                    draggingId,
                    draggingOverId: dragContainer?.id,
                    dragContainer,
                } : undefined}
                type={type ?? ACTION_MAP[row.actionId] ?? ActionContextType.Ticket}
                input={inputGroups ?? []}
                allowedActionIds={[]}
                isDragEnabled={!isEditing}
                onUpdate={() => {
                    const copy = Array.from(current);
                    setCurrent(copy);
                    onChange?.(copy);
                }}
                onCardCreate={editingItem => {
                    setIsEditing(editingItem != null);
                }}
                onDelete={() => {
                    // do nothin
                }}
                onEdit={() => {
                    // do
                    // nothin
                }}
            />
        </DragDropContainer>
    ));

    return isModal ? (
        <Confirm
            shouldShow
            size="xl"
            fullscreen="lg-down"
            dialogClassName="full-size-container bg-white shadow border-0 rounded position-static"
            keyboardContext="workflow"
            onHide={onHideModal}
            shouldShowFooter={false}
        >
            <>
                {onHideModal && (
                    <button
                        className="btn btn-none btn-lg p-0 position-absolute top-0 end-0 m-2"
                        type="button"
                        onClick={onHideModal}
                        aria-label="Expand the workflow display"
                    >
                        <BiCollapseAlt className="mt-n1 me-1" />
                    </button>
                )}
                <div className="p-2 position-relative" ref={parentDivRef}>
                    {display}
                </div>
            </>
        </Confirm>
    ) : (
        <div className="w-100 overflow-auto position-relative" ref={parentDivRef}>
            {display}
        </div>
    );
}

WorkflowDisplay.defaultProps = {
    isModal: false,
    input: undefined,
    onChange: undefined,
    value: undefined,
    shouldShowCreatingRequest: false,
    onHideModal: undefined,
    type: undefined,
};
